/**
 * 客户详情查看弹窗组件。
 *
 * 该组件以全屏模态弹窗的形式展示单个客户的完整信息，包含以下能力：
 * - 基本信息标签页：展示客户ID、公司名称、联系人、职位、电话、地址、国家、网站、备注等字段
 * - 公司信息标签页：展示公司补全状态（completed/failed/pending/未开始）、公司全称、行业、规模、社交链接
 * - 往来邮件标签页：按时间倒序展示该客户最近 10 封邮件，点击可内嵌查看邮件详情
 * - 联系人标签页：展示从邮件签名等渠道自动提取的联系人列表
 *
 * 设计要点：
 * - 通过 `show(customer)` 方法以命令式 API 打开弹窗，避免父组件维护 open 状态
 * - 邮件详情查看模式复用了 `email-viewer` 组件，通过 `viewingEmail` 状态切换
 * - 支持按 ESC 键分级关闭：先关闭邮件查看，再关闭整个弹窗
 * - 点击背景遮罩关闭弹窗，使用 mousedown 标记区分背景点击与内容拖拽
 *
 * 导出：`CrmCustomerViewModal`（自定义元素 `crm-customer-view-modal`）
 *
 * 依赖模块：`customerApi`、`emailApi`（数据获取）、`COUNTRIES`（国家映射）、`isSentByMe`（邮件方向判断）、`email-viewer`（邮件详情渲染）
 */
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import type { Customer, Email, CustomerContact } from '../types.js';
import { customerApi, emailApi } from '../api.js';
import { COUNTRIES } from '../utils/countries.js';
import { isSentByMe } from '../utils/email-helpers.js';
import './email-viewer.js';

/** 标签页类型枚举：info=基本信息，timeline=往来邮件，company=公司信息，contacts=联系人 */
type Tab = 'info' | 'timeline' | 'company' | 'contacts';

/**
 * 客户详情查看弹窗组件。
 *
 * 继承 `BaseElement`，通过 `@customElement('crm-customer-view-modal')` 注册为自定义元素。
 * 父组件可通过调用 `show(customer)` 方法打开弹窗并加载数据。
 */
@customElement('crm-customer-view-modal')
export class CrmCustomerViewModal extends BaseElement {
  /** 当前查看的客户对象，null 表示未选中任何客户 */
  @property({ type: Object }) customer: Customer | null = null;
  /** 弹窗是否打开，使用 reflect 使外部可通过属性控制显示 */
  @property({ type: Boolean, reflect: true }) open = false;
  /** 客户关联的邮箱地址列表，含是否主邮箱标记 */
  @state() private emails: Array<{ email: string; is_primary: number }> = [];
  /** 客户最近 10 封往来邮件的时间线数据 */
  @state() private timeline: Array<{ id: number; subject: string; email_date: number; send_from: any; folder: string }> = [];
  /** 客户的联系人列表（从邮件签名等渠道自动提取） */
  @state() private contacts: CustomerContact[] = [];
  /** 当前正在内嵌查看的邮件对象，null 表示处于标签页模式而非邮件查看模式 */
  @state() private viewingEmail: Email | null = null;
  /** 当前激活的标签页，默认为 'info'（基本信息） */
  @state() private tab: Tab = 'info';
  /** 标记 mousedown 是否发生在背景遮罩上，用于区分背景点击与内容区域点击 */
  private _backdropMousedown = false;

  /**
   * 关闭弹窗并重置所有内部状态。
   *
   * 清空邮箱列表、时间线、联系人和邮件查看状态，将标签页重置为"基本信息"。
   * 该方法同时被关闭按钮、ESC 键和背景点击调用。
   */
  close() {
    this.open = false;
    this.emails = [];
    this.timeline = [];
    this.contacts = [];
    this.viewingEmail = null;
    this.tab = 'info';
  }

  /**
   * ESC 键按下事件处理器（捕获阶段注册）。
   *
   * 实现分级关闭逻辑：
   * - 若正在查看邮件（viewingEmail 不为空），ESC 先关闭邮件查看回到标签页
   * - 若未在查看邮件，ESC 关闭整个弹窗
   *
   * 使用 `stopImmediatePropagation()` 阻止其他 keydown 监听器响应，避免 ESC 被父组件误处理。
   *
   * @param e - 键盘事件对象
   */
  private handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.open) {
      e.stopImmediatePropagation(); // 阻止其他监听器
      if (this.viewingEmail) {
        this.viewingEmail = null;
        this.requestUpdate();
      } else {
        this.close();
      }
    }
  };

  /** 生命周期：组件挂载时注册全局 keydown 监听（捕获阶段优先于冒泡） */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this.handleKeydown, true); // 捕获阶段
  }

  /** 生命周期：组件卸载时移除全局 keydown 监听，防止内存泄漏 */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.handleKeydown, true);
  }

  /**
   * 命令式打开弹窗并加载客户数据。
   *
   * 设置客户对象后并行请求客户邮箱列表和最近 10 封往来邮件，
   * 数据加载完成后将 `open` 设为 true 触发弹窗显示。
   * 请求失败时静默忽略（邮箱和时间线保持空数组），不影响弹窗打开。
   *
   * @param customer - 要查看的客户对象
   */
  async show(customer: Customer) {
    this.customer = customer;
    this.emails = [];
    this.contacts = [];
    this.tab = 'info';
    this.viewingEmail = null;
    try {
      // 并行请求客户邮箱列表和最近 10 封往来邮件，减少等待时间
      const [result, tlResult] = await Promise.all([
        customerApi.emails(customer.id),
        customerApi.timeline(customer.id, 10),
      ]);
      // 提取邮箱地址和主邮箱标记，丢弃其他字段
      this.emails = ((result as any).data || []).map((e: any) => ({ email: e.email, is_primary: e.is_primary }));
      // 截取前 10 条时间线数据
      this.timeline = (tlResult.data || []).slice(0, 10);
    } catch { /* ignore */ }
    this.open = true;
  }

  /**
   * 查看邮件详情内容。
   *
   * 通过邮件 ID 调用 `emailApi.get` 获取完整邮件对象并设置到 `viewingEmail`，
   * 触发弹窗切换到邮件查看模式。请求失败时静默忽略。
   *
   * @param emailId - 邮件 ID
   */
  private async viewEmailContent(emailId: number) {
    try {
      this.viewingEmail = await emailApi.get(emailId);
    } catch { /* ignore */ }
  }

  /**
   * 加载客户的联系人列表。
   *
   * 首次切换到"联系人"标签页时延迟加载，避免无谓的网络请求。
   * 请求失败时将 contacts 置为空数组并打印错误日志。
   */
  private async loadContacts() {
    if (!this.customer) return;
    try {
      this.contacts = await customerApi.contacts(this.customer.id);
    } catch (e) {
      console.error('Failed to load contacts:', e);
      this.contacts = [];
    }
  }

  /**
   * 将 Unix 时间戳（秒）格式化为本地化日期字符串。
   *
   * @param ts - Unix 时间戳（秒），0 或 falsy 返回 '-'
   * @returns 格式化后的中文日期字符串（如 "2024/1/15 14:30:00"）
   */
  private formatTimestamp(ts: number): string {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-CN');
  }

  /**
   * 渲染"基本信息"标签页内容。
   *
   * 以键值对列表形式展示客户的静态信息字段（公司名称、联系人、职位等），
   * 国家字段通过 `COUNTRIES` 数组映射为可读名称。
   * 邮箱列表单独渲染，主邮箱前显示对勾图标。
   */
  private renderInfoTab() {
    if (!this.customer) return html``;
    return html`
      <div class="space-y-3">
        ${[
          ['客户ID', String(this.customer.id)],
          ['公司名称', this.customer.company_name],
          ['联系人', this.customer.name],
          ['职位', this.customer.job_title],
          ['电话', this.customer.phone],
          ['地址', this.customer.address],
          ['国家', COUNTRIES.find(c => c.id === this.customer!.nation_id)?.name],
          ['网站', this.customer.website],
          ['备注', this.customer.remark],
        ].map(([label, value]) => html`
          <div class="flex justify-between items-start py-2 border-b border-border-light">
            <span class="text-sm text-text-muted font-medium">${label}</span>
            <span class="text-sm text-text text-right max-w-[60%]">${value || '-'}</span>
          </div>
        `)}
        <div class="py-2 border-b border-border-light">
          <span class="text-sm text-text-muted font-medium">邮箱地址</span>
          <div class="mt-2 space-y-1">
            ${this.emails.length === 0
              ? html`<span class="text-sm text-text-muted">-</span>`
              : this.emails.map(e => html`
                <div class="flex items-center gap-2 text-sm">
                  ${e.is_primary
                    ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" class="w-4 h-4 text-success flex-shrink-0"><path d="M20 6 9 17l-5-5"/></svg>`
                    : html`<span class="w-4 h-4 flex-shrink-0"></span>`}
                  <span class="text-text">${e.email}</span>
                </div>
              `)}
          </div>
        </div>
        <div class="flex justify-between items-start py-2">
          <span class="text-sm text-text-muted font-medium">创建时间</span>
          <span class="text-sm text-text">${this.formatTimestamp(this.customer.created_at)}</span>
        </div>
        <div class="flex justify-between items-start py-2">
          <span class="text-sm text-text-muted font-medium">更新时间</span>
          <span class="text-sm text-text">${this.formatTimestamp(this.customer.updated_at)}</span>
        </div>
      </div>
    `;
  }

  /**
   * 渲染"往来邮件"标签页内容。
   *
   * 以时间线列表形式展示客户最近 10 封邮件，每行包含日期、收发方向指示点（
   * sent/outbox 为蓝色 info，其余为绿色 success）和邮件主题。
   * 点击列表行调用 `viewEmailContent` 切换到邮件详情查看模式。
   */
  private renderTimelineTab() {
    return html`
      <div class="space-y-0">
        ${this.timeline.length === 0 ? html`
          <div class="text-sm text-text-muted py-8 text-center">暂无往来邮件</div>
        ` : this.timeline.map(t => html`
          <div class="flex items-center gap-3 text-sm py-2.5 border-b border-border-light last:border-0 cursor-pointer hover:bg-bg rounded px-2 transition-colors -mx-2"
            @click=${() => this.viewEmailContent(t.id)}>
            <span class="text-text-muted w-24 flex-shrink-0 text-xs">${this.formatTimestamp(t.email_date)}</span>
            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.folder === 'sent' || t.folder === 'outbox' ? 'bg-info' : 'bg-success'}"></span>
            <span class="text-text truncate flex-1 font-medium">${t.subject || '无主题'}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 text-text-muted flex-shrink-0"><path d="m9 18 6-6-6-6"/></svg>
          </div>
        `)}
      </div>
    `;
  }

  /**
   * 渲染"公司信息"标签页内容。
   *
   * 展示客户公司补全（enrichment）相关数据：
   * - 补全状态徽章：根据 enrichment_status 显示不同颜色和文案（已完成/失败/进行中/未开始）
   * - 公司全称、行业、公司规模等补全字段
   * - 社交链接和补全时间（仅在有值时显示）
   */
  private renderCompanyTab() {
    if (!this.customer) return html``;
    return html`
      <div class="space-y-3">
        <div class="flex justify-between items-start py-2 border-b border-border-light">
          <span class="text-sm text-text-muted font-medium">补全状态</span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
            ${this.customer.enrichment_status === 'completed' ? 'bg-success/10 text-success' :
              this.customer.enrichment_status === 'failed' ? 'bg-error/10 text-error' :
              this.customer.enrichment_status === 'pending' ? 'bg-warning/10 text-warning' :
              'bg-border text-text-muted'}">
            ${this.customer.enrichment_status === 'completed' ? '已完成' :
              this.customer.enrichment_status === 'failed' ? '失败' :
              this.customer.enrichment_status === 'pending' ? '进行中' : '未开始'}
          </span>
        </div>
        ${[
          ['公司全称', this.customer.company_full_name],
          ['行业', this.customer.industry],
          ['公司规模', this.customer.company_size],
        ].map(([label, value]) => html`
          <div class="flex justify-between items-start py-2 border-b border-border-light">
            <span class="text-sm text-text-muted font-medium">${label}</span>
            <span class="text-sm text-text text-right max-w-[60%]">${value || '-'}</span>
          </div>
        `)}
        ${this.customer.social_links ? html`
          <div class="py-2 border-b border-border-light">
            <span class="text-sm text-text-muted font-medium">社交链接</span>
            <div class="mt-1 text-sm text-text">${this.customer.social_links}</div>
          </div>
        ` : ''}
        ${this.customer.enriched_at ? html`
          <div class="flex justify-between items-start py-2">
            <span class="text-sm text-text-muted font-medium">补全时间</span>
            <span class="text-sm text-text">${this.formatTimestamp(this.customer.enriched_at)}</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * 渲染"联系人"标签页内容。
   *
   * 展示从邮件签名等渠道自动提取的联系人列表，每条记录包含：
   * 姓名、职位、来源标签（source）、邮箱和电话。
   * 数据为空时显示"暂无联系人"占位文案。
   */
  private renderContactsTab() {
    return html`
      <div class="space-y-0">
        ${this.contacts.length === 0 ? html`
          <div class="text-sm text-text-muted py-8 text-center">暂无联系人</div>
        ` : this.contacts.map(c => html`
          <div class="text-sm py-2.5 border-b border-border-light last:border-0 px-2">
            <div class="flex items-center gap-2">
              <span class="font-medium text-text">${c.name || '未知'}</span>
              ${c.job_title ? html`<span class="text-text-muted text-xs">${c.job_title}</span>` : ''}
              <span class="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-bg text-text-muted">${c.source}</span>
            </div>
            <div class="flex items-center gap-3 mt-1 text-xs text-text-secondary">
              ${c.email ? html`<span>${c.email}</span>` : ''}
              ${c.phone ? html`<span>${c.phone}</span>` : ''}
            </div>
          </div>
        `)}
      </div>
    `;
  }

  /**
   * 主渲染方法。
   *
   * 根据 `viewingEmail` 状态决定渲染模式：
   * - 邮件查看模式：显示返回按钮 + 邮件主题 + `<email-viewer>` 组件
   * - 标签页模式：显示标签栏（基本信息/公司信息/往来邮件/联系人）+ 对应标签页内容 + 底部关闭按钮
   *
   * 标签页切换到"联系人"时会延迟加载联系人数据（仅首次且列表为空时触发）。
   * 背景遮罩点击通过 mousedown 标记判断，避免拖拽内容时误关闭弹窗。
   *
   * @returns Lit 模板结果
   */
  render() {
    if (!this.open || !this.customer) return html``;
    const isViewing = !!this.viewingEmail;
    // 标签页配置数组：key 对应 Tab 类型，label 含数量统计，icon 为 SVG 图标
    const tabs: Array<{ key: Tab; label: string; icon: any }> = [
      { key: 'info', label: '基本信息', icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>` },
      { key: 'company', label: '公司信息', icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>` },
      { key: 'timeline', label: `往来邮件 (${this.timeline.length})`, icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>` },
      { key: 'contacts', label: `联系人 (${this.contacts.length})`, icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>` },
    ];

    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center z-[200] pt-2.5 pb-3" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.close(); }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-[1000px] max-h-[97vh] flex flex-col overflow-hidden border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>

          ${isViewing ? html`
            <!-- 邮件查看模式：显示返回按钮、邮件主题标题栏，底部嵌入 email-viewer 组件 -->
            <div class="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-border flex-shrink-0">
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors flex-shrink-0"
                @click=${() => { this.viewingEmail = null; this.requestUpdate(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <h2 class="m-0 font-semibold text-lg text-text truncate">${this.viewingEmail!.subject || '无主题'}</h2>
              <div class="flex-1"></div>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.close}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <div class="flex-1 overflow-y-auto">
              <email-viewer
                .email=${this.viewingEmail}
                .isSentByMe=${isSentByMe(this.viewingEmail)}
                .hideSubject=${true}
              ></email-viewer>
            </div>
          ` : html`
            <!-- 标签页模式：顶部标签栏切换不同信息视图，中间滚动区域显示对应标签页内容，底部固定关闭按钮 -->
            <div class="flex items-center justify-between px-6 pt-5 pb-0 border-b border-border flex-shrink-0">
              <div class="flex gap-0">
                ${tabs.map(t => html`
                  <button class="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer
                    ${this.tab === t.key ? 'text-text border-[#000]' : 'text-text-muted border-transparent hover:text-text'}"
                    @click=${() => { this.tab = t.key; if (t.key === 'contacts' && !this.contacts.length) this.loadContacts(); }}>
                    ${t.icon}
                    ${t.label}
                  </button>
                `)}
              </div>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.close}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <div class="flex-1 overflow-y-auto p-6">
              ${this.tab === 'info' ? this.renderInfoTab() :
                this.tab === 'company' ? this.renderCompanyTab() :
                this.tab === 'timeline' ? this.renderTimelineTab() :
                this.renderContactsTab()}
            </div>
            <div class="flex justify-end px-6 pb-5 pt-2 border-t border-border flex-shrink-0">
              <button class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.close}>关闭</button>
            </div>
          `}
        </div>
      </div>
    `;
  }
}
